import React, { FC, useEffect, useState } from 'react';
import { usePlayer } from 'providers/player/PlayerContext';
import { Broadcast, Episode, Movie, MovieDetails, ProgramDetails, RecordingDetails, TVSeriesDetailEpisode, Series } from 'types/Asset';
import { SubscribeData, TVAssetEntitlementState, VODAssetEntitlementState } from 'types/Entitlement';
import { StyledButton } from 'components/Pages/PageDetailCommon/PageDetailCommon.css';
import translate from 'utils/fnTranslate';
import { useUpsellFlow } from 'providers/useUpsellFlow/UpsellFlowContext';
import { useAuth } from 'providers/useAuth/AuthContext';
import {
    isBroadcast,
    isEpisode,
    isMovie,
    isTVOD,
    isMovieDetails,
    isProgramMovie,
    isProgramDetails,
    isRecordingDetails,
    isSeries,
    isTvSeriesEpisode,
    isSVOD,
    isVODSeriesEpisode,
} from 'utils/fnTypeGuards';
import { useConfig } from 'providers/useConfig/ConfigContext';
import { MILLISECONDS } from 'utils/TimeUnit';
import { useTVAssetEntitlementState } from 'hooks/useTVAssetEntitlementState/useTVAssetEntitlementState';
import { useSubscription } from 'providers/useSubscription/SubscriptionContext';
import { usePlayOptionHelper } from 'providers/usePlayOptions/PlayOptionsContext';
import { isFuture, isNow } from 'utils/fnDate';
import { getCurrencySymbol, isEpisodeTrailer } from 'utils/fnData';
import { StyledSVG } from 'style/styled-components/reusable.css';
import icons from 'style';
import { LoadingIndicator } from 'components/LoadingIndicator/LoadingIndicator';
import { usePersistentState } from 'hooks/useStorage/useStorage';
import { useVodPurchasesQuery } from 'hooks/useVodPurchasesQuery';
import { SERVICE_ACTIVATED_EVENT } from 'hooks/useServiceActivationWatcher/useServiceActivationWatcher';
import { getPlayerPropsForUrl } from 'hooks/usePlayerRouter';

type PrimaryButtonAssetType =
    MovieDetails | ProgramDetails | RecordingDetails | Episode | Movie | Broadcast | TVSeriesDetailEpisode | Series;
type PropTypes = {
    asset: PrimaryButtonAssetType;
    path?: string;
    screenName?: string;
    entitlementState?: string;
};

/**
 * Smart CTA button which wraps a normal styled button and based on the entitlement state renders different actions / links / texts to the button
 *
 * @param asset
 * @param path
 * @param screenName
 * @constructor
 */
export const CTAButton: FC<PropTypes> = ({ asset, path, screenName, entitlementState }) => {
    const { initUpsellFlow } = useUpsellFlow();
    const { initSubscribeFlow } = useSubscription();
    const { isGuest, isLoggedIn, isInhibited } = useAuth();
    const { config } = useConfig();
    const playOptionVerifier = usePlayOptionHelper();
    const { isMini, updateMiniPlayerProps } = usePlayer();
    const tvCheckEntitlement = useTVAssetEntitlementState();
    const { store: playbackPositions } = usePersistentState('playbackPositions', {});
    const { data: vodPurchasesData } = useVodPurchasesQuery(Boolean((asset as { vod_terms?: unknown[] })?.vod_terms?.length));
    const [serviceActivated, setServiceActivated] = useState(false);

    useEffect(() => {
        const handleServiceActivated = () => setServiceActivated(true);
        window.addEventListener(SERVICE_ACTIVATED_EVENT, handleServiceActivated);
        return () => window.removeEventListener(SERVICE_ACTIVATED_EVENT, handleServiceActivated);
    }, []);

    const getAssetTypeAndId = (assetItem: any) => {
        if (!assetItem) return {};

        const isProgramSeries = assetItem ? 'program_series_id' in assetItem && assetItem?.program_series_id !== '' : undefined;
        const isRecording = assetItem.type === 'recording';

        const assetType = isRecording || isProgramSeries ? 'listing_playback_positions' : 'vod_asset_playback_positions';
        const id = isProgramSeries ? assetItem?.listings?.[0]?.id : assetItem?.id;

        return { assetType, id };
    };

    const { assetType, id } = getAssetTypeAndId(asset);
    const bookmark = playbackPositions?.[assetType]?.[id] || 0;

    const playInMiniPlayer = () => {
        const playerProps = getPlayerPropsForUrl(path);

        if (playerProps) {
            updateMiniPlayerProps(playerProps);
        }
    };

    const onVodUpsellClick = () => {
        if (isEpisode(asset) || isMovie(asset) || isMovieDetails(asset) || isTVOD(asset)) {
            initUpsellFlow(path, asset);
        }
    };

    const onSubscribeClick = () => {
        let subscribeData: SubscribeData;
        if (isSeries(asset)) {
            return;
        }

        if (isRecordingDetails(asset)) {
            // NO-OP
            return;
        }

        // TV
        if (isProgramDetails(asset) || isBroadcast(asset) || isTvSeriesEpisode(asset) || isProgramMovie(asset)) {
            let entitlement = entitlementState ?? null;

            if (!entitlement && asset?.listings && asset.listings.length > 0) {
                entitlement = tvCheckEntitlement(asset.listings[0]);
            }

            switch (entitlement) {
                case TVAssetEntitlementState.SHOULD_BUY_BASIC_REPLAY_WINDOW:
                case TVAssetEntitlementState.SHOULD_BUY_EXTENDED_REPLAY_WINDOW:
                case TVAssetEntitlementState.SHOULD_SUBSCRIBE_CHANNEL:
                    subscribeData = {
                        type: 'Listing',
                        code: asset?.listings[0]?.id,
                    };

                    break;
                default: {
                    // no - op

                    break;
                }
            }
        } else {
            // SVOD

            subscribeData = {
                type: 'Product',
                code: asset?.id,
                metaData: asset,
            };
        }

        if (subscribeData) {
            initSubscribeFlow(path, subscribeData);
        }
    };

    const getProgress = (): number => {
        if (isSeries(asset)) {
            return null;
        }

        if (isProgramDetails(asset) || isBroadcast(asset) || isTvSeriesEpisode(asset)) {
            if (isNow(asset.startTime, asset.endTime) && asset.bookmark > MILLISECONDS.toSeconds(Date.now() - asset.startTime)) {
                return 0;
            }

            if (isTvSeriesEpisode(asset)) {
                if (asset?.duration) {
                    const progress = (bookmark * 100) / asset.duration;
                    return progress;
                }
                if (asset?.listings?.[0]?.duration) {
                    const progress = (bookmark * 100) / asset.listings[0].duration;
                    return progress;
                }
            }
        }

        if (isTVOD(asset)) {
            return (bookmark * 100) / asset.duration;
        }

        let duration: number;
        if (isProgramDetails(asset) || isBroadcast(asset) || isTvSeriesEpisode(asset)) {
            if (asset?.startTime && asset?.endTime) {
                duration = MILLISECONDS.toSeconds(asset.endTime - asset.startTime);
            } else {
                duration = asset?.listings?.[0]?.duration ?? 0;
            }
        } else {
            duration = asset?.duration;
        }

        return duration > 0 ? (bookmark * 100) / duration : 0;
    };

    const hasProgress = (): boolean => {
        const progress = getProgress();
        return progress >= 0.02 && progress <= 98; // 98% is the threshold for the progress to be considered as "watched" && 0.02% is the threshold for the progress to be considered as "not watched"
    };

    const getEpisodeButtonText = (episode: Episode | TVSeriesDetailEpisode | (Episode | TVSeriesDetailEpisode)[]) => {
        let episodeToUse;

        if (Array.isArray(episode)) {
            [episodeToUse] = episode;
        } else {
            episodeToUse = episode;
        }

        if (isEpisodeTrailer(episodeToUse)) {
            return `${translate('SCREEN_DETAIL_PLAY_BUTTON')}`;
        }

        return `${translate(hasProgress() ? 'SCREEN_DETAIL_RESUME_BUTTON' : 'SCREEN_DETAIL_PLAY_BUTTON')}${
            episodeToUse.season_number > 0 ? ` ${translate('HOVER_SEASON')}${episodeToUse.season_number}` : ''
        }${episodeToUse.episode_number ? ` ${translate('HOVER_EPISODE')}${episodeToUse.episode_number}` : ''}`;
    };

    let watchLink = null;
    let clickListener = null;
    let buttonText = null;
    let isEntitled = false;

    if (!asset) {
        return null;
    }

    if (isInhibited) {
        return null;
    }

    if (isSeries(asset)) {
        return null;
    }

    if (isRecordingDetails(asset)) {
        if (asset.start && isFuture(asset.start * 1000)) {
            return null;
        }

        buttonText = translate(hasProgress() ? 'SCREEN_DETAIL_RESUME_BUTTON' : 'SCREEN_DETAIL_PLAY_BUTTON');
        isEntitled = true;
        if (isMini) {
            watchLink = null;
            clickListener = playInMiniPlayer;
        } else {
            watchLink = path;
            clickListener = null;
        }

        if (isTvSeriesEpisode(asset)) {
            buttonText = getEpisodeButtonText(asset);
        }
    } else if (isProgramDetails(asset) || isBroadcast(asset) || isTvSeriesEpisode(asset) || isRecordingDetails(asset)) {
        if (!entitlementState && asset?.listings && asset.listings.length > 0) {
            entitlementState = tvCheckEntitlement(asset.listings?.[0]);
        }

        if (asset.vod_terms && asset.vod_terms.some((term) => term.start != null)) {
            entitlementState = TVAssetEntitlementState.ENTITLED;
        }

        if (serviceActivated) {
            entitlementState = TVAssetEntitlementState.ENTITLED;
        }

        if (entitlementState === TVAssetEntitlementState.ENTITLED) {
            isEntitled = true;
        }

        switch (entitlementState) {
            case TVAssetEntitlementState.ENTITLED:
                buttonText = translate(hasProgress() ? 'SCREEN_DETAIL_RESUME_BUTTON' : 'SCREEN_DETAIL_PLAY_BUTTON');

                if (isNow(MILLISECONDS.toSeconds(asset.startTime), MILLISECONDS.toSeconds(asset.endTime))) {
                    if (isMini) {
                        watchLink = null;
                        clickListener = playInMiniPlayer;
                    } else {
                        watchLink = path;
                        clickListener = null;
                    }
                } else {
                    watchLink = null;
                    clickListener = () => {
                        playOptionVerifier(asset, path);
                        return undefined;
                    };
                }

                if (isTvSeriesEpisode(asset)) {
                    buttonText = getEpisodeButtonText(asset);
                }
                break;
            case TVAssetEntitlementState.SHOULD_BUY_BASIC_REPLAY_WINDOW:
            case TVAssetEntitlementState.SHOULD_BUY_EXTENDED_REPLAY_WINDOW:
                clickListener = onSubscribeClick;
                buttonText = translate('ORDER_BUTTON');

                break;

            case TVAssetEntitlementState.SHOULD_SUBSCRIBE_CHANNEL:
                clickListener = onSubscribeClick;
                buttonText = translate('ORDER_BUTTON');

                break;
            default: {
                watchLink = null;
                buttonText = null;

                break;
            }
        }

        if (
            entitlementState !== TVAssetEntitlementState.ENTITLED &&
            (isProgramDetails(asset) || isTvSeriesEpisode(asset)) &&
            asset.isRecorded &&
            entitlementState !== TVAssetEntitlementState.SHOULD_BUY_BASIC_REPLAY_WINDOW
        ) {
            buttonText = translate(hasProgress() ? 'SCREEN_DETAIL_RESUME_BUTTON' : 'SCREEN_DETAIL_PLAY_BUTTON');
            isEntitled = true;
            watchLink = null;
        }
    } else if (isProgramMovie(asset) && (asset as any).type?.toLowerCase() !== 'tvod') {
        if (asset?.listings && asset.listings.length > 0) {
            entitlementState = tvCheckEntitlement(asset.listings?.[0]);
        }
        if (serviceActivated) {
            entitlementState = TVAssetEntitlementState.ENTITLED;
        }
        if (entitlementState === TVAssetEntitlementState.ENTITLED) {
            buttonText = translate(hasProgress() ? 'SCREEN_DETAIL_RESUME_BUTTON' : 'SCREEN_DETAIL_PLAY_BUTTON');
            watchLink = null;
            clickListener = () => playOptionVerifier(asset, path);
            isEntitled = true;
        } else if (entitlementState === TVAssetEntitlementState.SHOULD_SUBSCRIBE_CHANNEL) {
            clickListener = onSubscribeClick;
            buttonText = translate('ORDER_BUTTON');
        }
    } else {
        const price = asset.price ?? asset.vod_terms?.find((term) => term.price)?.price;

        // Check if user is entitled via vod_session_until (has active subscription/rental)
        if (asset.vod_session_until != null) {
            asset.vodAssetState = VODAssetEntitlementState.ENTITLED;
            isEntitled = true;
        }

        if (!isEntitled && vodPurchasesData?.results) {
            const activePurchase = vodPurchasesData.results.find(
                (purchase: any) => purchase.vod_asset?.id === asset.id && isFuture(purchase.valid_until * 1000)
            );
            if (activePurchase) {
                asset.vodAssetState = VODAssetEntitlementState.ENTITLED;
                isEntitled = true;
            }
        }

        if (serviceActivated) {
            asset.vodAssetState = VODAssetEntitlementState.ENTITLED;
            isEntitled = true;
        }

        if (asset.vodAssetState === VODAssetEntitlementState.ENTITLED) {
            isEntitled = true;
        }

        const hasTvodTerms = asset.vod_terms?.some((term) => term.type === 'TVOD');

        const hasBothRentAndSubscribe = hasTvodTerms && asset.additional_services_available;

        if (asset.vodAssetState !== VODAssetEntitlementState.ENTITLED) {
            if (hasTvodTerms) {
                asset.vodAssetState = VODAssetEntitlementState.SHOULD_RENT;
            } else if (asset.additional_services_available) {
                asset.vodAssetState = VODAssetEntitlementState.SHOULD_SUBSCRIBE;
            }
        }

        switch (asset.vodAssetState) {
            case VODAssetEntitlementState.ENTITLED:
                if (isMini) {
                    watchLink = null;
                    clickListener = playInMiniPlayer;
                } else {
                    watchLink = path;
                }

                if (isEpisode(asset) || isVODSeriesEpisode(asset)) {
                    buttonText = getEpisodeButtonText(asset);
                } else if (isTVOD(asset) || isMovie(asset) || isSVOD(asset)) {
                    buttonText = translate(hasProgress() ? 'SCREEN_DETAIL_RESUME_BUTTON' : 'SCREEN_DETAIL_PLAY_BUTTON');

                    const ep = asset as { season_number?: number; episode_number?: number; episode_title?: string; title?: string };
                    if (!isEpisodeTrailer(ep as Episode) && (ep.season_number || ep.episode_number)) {
                        buttonText += `${ep.season_number > 0 ? ` ${translate('HOVER_SEASON')}${ep.season_number}` : ''}${
                            ep.episode_number ? ` ${translate('HOVER_EPISODE')}${ep.episode_number}` : ''
                        }`;
                    }
                }

                break;
            case VODAssetEntitlementState.SHOULD_RENT:
                clickListener = onVodUpsellClick;

                if (hasBothRentAndSubscribe) {
                    buttonText = translate('ORDER_BUTTON');
                } else if (price === 0) {
                    buttonText = translate(isEpisode(asset) ? 'RENT_EPISODE_FOR_FREE_BUTTON' : 'RENT_FOR_FREE_BUTTON');
                } else if (price > 0) {
                    buttonText = translate(isEpisode(asset) ? 'RENT_EPISODE_FOR_PRICE_BUTTON' : 'RENT_FOR_PRICE_BUTTON', {
                        price_euros: `${price}${getCurrencySymbol(asset.vod_terms[0].currency)}`,
                    });
                }

                break;
            case VODAssetEntitlementState.SHOULD_BUY:
                clickListener = onVodUpsellClick;

                break;
            case VODAssetEntitlementState.SHOULD_SUBSCRIBE:
                clickListener = onSubscribeClick;
                buttonText = translate('ORDER_BUTTON');

                break;
            default: {
                // do nothing -> no primary button
            }
        }
    }

    if (isGuest || !isLoggedIn) {
        watchLink = null;
        buttonText = translate('SCREEN_SIGNUP_BUTTON');
        clickListener = () => {
            window.open(
                window.location.pathname.includes('/huub')
                    ? config.app_config.miscellanious.sign_up_link_huub
                    : config.app_config.miscellanious.sign_up_link
            );
        };
    }

    if ((watchLink || clickListener) && buttonText) {
        return (
            <>
                <LoadingIndicator overlay={true} isLoading={/* loadingRecordingId */ false} />
                <StyledButton
                    key={'cta'}
                    label={buttonText}
                    isPrimary={true}
                    link={watchLink}
                    onClick={clickListener}
                    data-event-name={'click'}
                    data-track-id={'button_cta'}
                    data-test-id={'button_cta'}
                    data-button-title={buttonText}
                    data-page-name={screenName}
                    className={'full-width-on-mobile'}
                    progress={hasProgress() && !isGuest && isEntitled ? getProgress() : 0}
                >
                    {!isGuest && <StyledSVG className="show-label" svg={isEntitled ? icons.playIconDark : icons.cartIcon} />}
                </StyledButton>
            </>
        );
    }

    return null;
};
